
  import { computed, defineComponent, ref, watch, inject, Ref } from 'vue';
  import { Loader } from '@/ui/components/Loader';
  import { Box } from '@/ui/components/Box';
  import { InputField } from '@/ui/components/form/InputField';
  import { ResourceTable } from '@/ui/components/table/ResourceTable';
  import { du } from '@/ui/i18n/i18n';
  import { toFixed } from '@/ui/helpers/numberHelper';
  import { useDetailedView, useDetailedViewFilters } from '@/ui/concerns/analytics/useDetailedView';
  import { useDetailedViewStore } from '@/stores/analytics/detailedView';
  import { ReportRecordData, ReportWidget } from '@/services/api/resources/analytics-builder/model';
  import { reportFromAnalyticsPageKey } from '@/ui/injectionKeys';
  import StaticPagination from '@/ui/components/StaticPagination.vue';
  import { openNewRouterTab } from '@/ui/helpers';
  import { Tooltip } from '@/ui/components/Tooltip';
  import ClampedTriggerTooltip from '@/ui/components/ClampedTriggerTooltip.vue';
  import { Icon } from '@/ui/components/icon/Icon';
  import Context from '@/ui/components/analytics/detailedView/Context.vue';
  import ReportExport from '@/ui/components/analytics/ReportExport.vue';
  import { useExports } from '@/ui/concerns/analytics/exports/useExports';
  import { SupportedExportFormat } from '@/services/api/resources/analytics';
  import { user } from '@/ui/auth/auth';
  import { ResponsesTypes } from '@/entities/checklist';
  import { useWidgetFormItem } from '@/ui/concerns/analytics/widget';
  import { isTbrEnabled } from '@/ui/helpers/featureHelper';
  import FileItemsModal from '@/ui/components/analytics/modals/FileItemsModal.vue';

  export default defineComponent({
    components: {
      FileItemsModal,
      ReportExport,
      Loader,
      Box,
      InputField,
      ResourceTable,
      Pagination: StaticPagination,
      Tooltip,
      ClampedTriggerTooltip,
      Icon,
      Context,
    },
    setup() {
      const isFilesModalOpened = ref(false);
      const fileModalData = ref<string | null>(null);

      const { getDetailedViewDataTask, data } = useDetailedView();
      const { prepareFilters } = useDetailedViewFilters();
      const { isDetailedViewExportLoading, exportDetailedView } = useExports();
      const store = useDetailedViewStore();
      const report = inject<Ref<ReportRecordData | null>>(reportFromAnalyticsPageKey, ref(null));

      const search = ref('');
      const page = ref(1);
      const perPage = 20;

      const widget = computed(() => report.value?.widgets.find(widget => widget.key === store.widgetKey));
      const combinedFilters = computed(() => prepareFilters(
        widget.value!.filters,
        report.value!.filters,
        widget.value!.attribute,
        store.resourceId,
        store.options,
      ));
      const taskParams = computed(() => ({
        filters: combinedFilters.value,
        metric: widget.value!.metric,
        attribute: widget.value!.attribute,
        page: page.value,
        perPage,
        searchTerm: search.value,
      }));

      const { downloadAsset, countAssets } = useWidgetFormItem(widget.value as ReportWidget);

      const onPageChanged = (value: number) => {
        page.value = value;
      };

      const updateSearch = (value: string) => {
        search.value = value;
      };

      const isDownloadableResponse = (type: string) => [
        ResponsesTypes.photo,
        ResponsesTypes.signature,
        ResponsesTypes.annotation,
        ResponsesTypes.file,
      ].includes(type as ResponsesTypes);

      const isPhotoResponse = (type: string) => type === ResponsesTypes.photo;

      const isFileResponse = (type: string) => type === ResponsesTypes.file;

      watch([page, search], () => getDetailedViewDataTask.value.run(taskParams.value));

      const onRowClick = ({ inspection_id }: { inspection_id: number }) => {
        openNewRouterTab({ name: 'inspectionDetail', params: { id: inspection_id.toString() } });
      };

      const onExport = (format: SupportedExportFormat) => {
        exportDetailedView(taskParams.value, format, user.value.timezone).run();
      };

      const onDownloadClick = (value: string) => {
        downloadAsset(value);
      };

      const showFiles = (value: string) => {
        fileModalData.value = value;
        isFilesModalOpened.value = true;
      };

      const closeFilesModal = () => {
        isFilesModalOpened.value = false;
        fileModalData.value = null;
      };

      return {
        isFilesModalOpened,
        fileModalData,
        search,
        page,
        perPage,
        widget,
        onPageChanged,
        updateSearch,
        getDetailedViewDataTask,
        taskParams,
        data,
        toFixed,
        du,
        attribute: widget.value?.attribute,
        onRowClick,
        isDetailedViewExportLoading,
        onExport,
        isDownloadableResponse,
        isPhotoResponse,
        isFileResponse,
        onDownloadClick,
        countAssets,
        isTbrEnabled,
        showFiles,
        closeFilesModal,
      };
    },
  });
